import * as React from 'react';

import NumberFormat from 'react-number-format';
import { Field } from 'redux-form';

import red from '@material-ui/core/colors/red';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Grid from '@material-ui/core/Grid';
import InputAdornment from '@material-ui/core/InputAdornment';
import Radio from '@material-ui/core/Radio';
import { withStyles, createStyles, WithStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';

import type { JobFieldProps } from './job-field';

import { UiButton } from '../../ui/ui-button';

import { JobField } from './job-field';

import {
  RadioGroup,
  TextField,
  Checkbox,
} from '../../widgets/redux-form-fields/material-ui-wrappers';

const styles = () =>
  createStyles({
    radioGroup: {
      display: 'inline',
      width: '100%',
      paddingTop: '0px',
    },
    error: {
      color: red[500],
    },
    compensationField: {
      margin: '8px',
      width: '30%',
    },
    checkbox: {
      marginRight: '8px',
    },
  });

interface Props extends JobFieldProps, WithStyles<typeof styles> {
  job?: Partial<Job>;
  change: Function;
}

type State = {
  compensationType: string | null;
  compensationLow: string | null;
  compensationHigh: string | null;
  warnings: Record<string, any> | null;
};

const COMP_TYPES = {
  HOURLY: {
    key: 'HOURLY',
    name: 'Hourly',
    endAdornment: '/hr',
  },
  ANNUAL: {
    key: 'ANNUAL',
    name: 'Annual',
    endAdornment: '/yr',
  },
};

type NumberFormatProps = {
  inputRef: (inputRef: any) => void;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
};

const NumberFormatCustom = (props: NumberFormatProps) => {
  const { inputRef, ...other } = props;
  return (
    <NumberFormat
      {...other}
      getInputRef={inputRef}
      thousandSeparator
      decimalScale={2}
      fixedDecimalScale
    />
  );
};

const normalizeToNumber = (value: string) => Number(value) || null;

class JobFieldCompensationComponent extends React.PureComponent<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      compensationType: props.job?.compensationType ?? COMP_TYPES.HOURLY.key,
      compensationLow: props.job?.compensationLow?.toString() ?? null,
      compensationHigh: props.job?.compensationHigh?.toString() ?? null,
      warnings: null,
    };
  }

  handleRadioChange = (_event: React.ChangeEvent<HTMLInputElement>, newValue?: string) => {
    const compensationType = newValue ?? COMP_TYPES.HOURLY.key;
    this.setState({
      compensationType,
      warnings: null,
    });
    this.props.change('compensationType', compensationType);
    this.props.change('compensationLow', null);
    this.props.change('compensationHigh', null);
  };

  handleClear = () => {
    this.setState({ warnings: {} });
    this.props.change('compensationType', null);
    this.props.change('compensationLow', null);
    this.props.change('compensationHigh', null);
  };

  handleCompensationChange = (name: keyof State) => (e: any) => {
    this.setState(
      {
        [name]: e.target.value,
      } as Record<keyof State, any>,
      () => {
        const { compensationType } = this.state;
        const { compensationLow, compensationHigh } = this.state;
        if (compensationHigh === '') {
          // compensationHigh is not required if you have compensationLow. We use this branch to
          // clear out warnings when removing compensationHigh
          this.setState({ warnings: {} });
        } else if (compensationHigh && compensationLow && compensationType) {
          const numberCompensationLow = normalizeToNumber(compensationLow.replace(/,/g, ''));
          const numberCompensationHigh = normalizeToNumber(compensationHigh.replace(/,/g, ''));
          if (!numberCompensationHigh || !numberCompensationLow) {
            return;
          }

          const tooLowError = `This seems low for ${compensationType.toLowerCase()} pay. Are you sure?`;
          const tooHighError = `This seems high for ${compensationType.toLowerCase()} pay. Are you sure?`;
          const unacceptableRangeError =
            'Please enter a "to" value that is above your "from" value.';
          let warnings = {};

          if (numberCompensationLow > numberCompensationHigh) {
            warnings = {
              compensationLow: unacceptableRangeError,
            };
          } else if (compensationType === COMP_TYPES.HOURLY.key) {
            // Add a warning if the comp provided is greater than 2 digits
            warnings = {
              compensationLow: numberCompensationLow / 100 >= 1 ? tooHighError : undefined,
              compensationHigh: numberCompensationHigh / 100 >= 1 ? tooHighError : undefined,
            };
          } else if (compensationType === COMP_TYPES.ANNUAL.key) {
            // Add a warning if the comp provided is less than 5 digits
            warnings = {
              compensationLow: numberCompensationLow / 10000 < 1 ? tooLowError : undefined,
              compensationHigh: numberCompensationHigh / 10000 < 1 ? tooLowError : undefined,
            };
          }
          this.setState({
            warnings,
          });
        }
      },
    );
  };

  render() {
    const { classes, job } = this.props;
    const { warnings, compensationType, compensationLow, compensationHigh } = this.state;
    const { HOURLY, ANNUAL } = COMP_TYPES;
    const { classes: _, ...childrenProps } = this.props;

    let isRequired = false;
    if (job) {
      isRequired = job.state === 'CO' && !compensationLow && !compensationHigh;
    }

    if (!job?.compensationType && compensationType) {
      this.props.change('compensationType', compensationType);
    }

    if (!job?.shouldPublishCompensation && isRequired) {
      this.props.change('shouldPublishCompensation', true);
    }

    return (
      <JobField
        {...childrenProps}
        title="Compensation Information"
        caption={
          !(job?.state === 'CO') ? (
            "By default, we do not post offered compensation information and only use this information to gauge whether an applicant's salary expectations match what is offered. Check 'Make visible to applicants' if you want to disclose this information."
          ) : (
            <div>
              Job postings in the state of Colorado are required by law to include compensation
              information. Read more{' '}
              <a
                className="typo-link"
                target="_blank"
                rel="noopener noreferrer"
                href="https://leg.colorado.gov/bills/sb19-085"
              >
                here
              </a>
              .
            </div>
          )
        }
        editView={
          <>
            <Field
              className={classes.radioGroup}
              name="compensationType"
              component={RadioGroup}
              onChange={this.handleRadioChange}
            >
              <FormControlLabel value={HOURLY.key} control={<Radio />} label={HOURLY.name} />
              <FormControlLabel value={ANNUAL.key} control={<Radio />} label={ANNUAL.name} />
            </Field>
            <Grid item xs={12}>
              {warnings && (warnings.compensationLow || warnings.compensationHigh) && (
                <Typography variant="body2" className={classes.error}>
                  {warnings.compensationLow || warnings.compensationHigh}
                </Typography>
              )}
              <Field
                className={classes.compensationField}
                name="compensationLow"
                label="From"
                normalize={normalizeToNumber}
                parse={(val: string) => parseFloat(val.replace(/,/g, ''))}
                error={Boolean(warnings && warnings.compensationLow)}
                required={isRequired}
                component={TextField}
                onChange={this.handleCompensationChange('compensationLow')}
                InputProps={{
                  inputComponent: NumberFormatCustom,
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  endAdornment: (
                    <InputAdornment position="end">
                      {compensationType === ANNUAL.key ? '/yr' : '/hr'}
                    </InputAdornment>
                  ),
                }}
              />
              <Field
                className={classes.compensationField}
                name="compensationHigh"
                error={Boolean(warnings && warnings.compensationHigh)}
                required={isRequired}
                label="To"
                parse={(val: string) => parseFloat(val.replace(/,/g, ''))}
                normalize={normalizeToNumber}
                component={TextField}
                onChange={this.handleCompensationChange('compensationHigh')}
                InputProps={{
                  inputComponent: NumberFormatCustom,
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  endAdornment: (
                    <InputAdornment position="end">
                      {compensationType === ANNUAL.key ? '/yr' : '/hr'}
                    </InputAdornment>
                  ),
                }}
              />
              {!(job?.state === 'CO') ? (
                <>
                  <Field
                    name="shouldPublishCompensation"
                    component={Checkbox}
                    className={classes.checkbox}
                    label="Make visible to applicants"
                  />
                  <UiButton
                    variant="tertiary"
                    size="xxs"
                    onClick={this.handleClear}
                    disabled={isRequired}
                  >
                    Clear Compensation Information
                  </UiButton>
                </>
              ) : null}
            </Grid>
          </>
        }
      />
    );
  }
}

export const JobFieldCompensation = withStyles(styles)(JobFieldCompensationComponent);
